import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { userVersionFileService } from '../../services/userVersionFileService';

const validationSchema = Yup.object().shape({
  version: Yup.string()
    .required('Version is required'),
  changelog: Yup.string()
    .nullable(),
  file_path: Yup.string()
    .required('File name is required'),
  is_latest: Yup.boolean()
});

const initialValues = (version) => ({
  id: version?.id || null,
  version: version?.version || '',
  changelog: version?.changelog || '',
  file_path: version?.file_path || '',
  file_size: version?.file_size || null,
  is_latest: version?.is_latest || false
});

const VersionFormDialog = ({ open, onClose, onSave, version, productId }) => {
  const [lsUsers, setLsUsers] = useState([]);
  const [lsFiles, setLsFiles] = useState({});

  useEffect(() => {
    if (!open || !productId) return;
    let cancelled = false;
    (async () => {
      try {
        const data =
          await userVersionFileService.getProductLicenseServerUsers(productId);
        if (cancelled) return;
        setLsUsers(data.users || []);

        // Prefill from existing registrations when editing
        if (version?.id) {
          const status = await userVersionFileService.getVersionStatus(
            version.id,
          );
          if (cancelled) return;
          setLsFiles(
            Object.fromEntries(
              status.users
                .filter((u) => u.file)
                .map((u) => [u.id, u.file.file_path]),
            ),
          );
        } else {
          setLsFiles({});
        }
      } catch (err) {
        console.error("Error loading license server users:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, productId, version]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await onSave({
        ...values,
        license_server_files: lsUsers
          .filter((u) => (lsFiles[u.id] || "").trim())
          .map((u) => ({ userId: u.id, file_path: lsFiles[u.id].trim() })),
        isNew: !version
      });
      onClose();
    } catch (error) {
      console.error('Error submitting version:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {version ? 'Edit Version' : 'Add New Version'}
      </DialogTitle>
      <Formik
        initialValues={initialValues(version)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Field
                  name="version"
                  as={TextField}
                  label="Version"
                  fullWidth
                  margin="normal"
                  error={touched.version && Boolean(errors.version)}
                  helperText={
                    (touched.version && errors.version) || 
                    'Enter version in format x.y.z or x.y.z.w (e.g., 1.1.1 or 1.1.1.0)'
                  }
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Field
                  name="changelog"
                  as={TextField}
                  label="Changelog"
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                  error={touched.changelog && Boolean(errors.changelog)}
                  helperText={touched.changelog && errors.changelog}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Field
                  name="file_path"
                  as={TextField}
                  label="File Name"
                  fullWidth
                  margin="normal"
                  placeholder="e.g., product.1.2.3.zip"
                  error={touched.file_path && Boolean(errors.file_path)}
                  helperText={
                    (touched.file_path && errors.file_path) || 
                    'Enter the zip file name (e.g., product.1.2.3.zip)'
                  }
                />
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Note: File must be a zip file and already exist in the storage/products directory.
                  Only alphanumeric characters and dots allowed in the name.
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_latest"
                      checked={values.is_latest}
                      onChange={(e) => {
                        setFieldValue('is_latest', e.target.checked);
                      }}
                    />
                  }
                  label="Set as Latest Version"
                />
              </Box>

              {lsUsers.length > 0 && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      License server user zips ({lsUsers.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Optional. Leave blank to register later. Each file must
                      already exist in storage/products/&lt;userId&gt;/.
                    </Typography>
                    {lsUsers.map((u) => (
                      <TextField
                        key={u.id}
                        label={u.email}
                        fullWidth
                        margin="dense"
                        placeholder="e.g., product.1.2.3.zip"
                        value={lsFiles[u.id] ?? ""}
                        onChange={(e) =>
                          setLsFiles((f) => ({ ...f, [u.id]: e.target.value }))
                        }
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {version ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default VersionFormDialog;